import getBbox from '@turf/bbox'
import getCentroid from '@turf/centroid'
import { Formik } from 'formik'
import { get } from 'idb-keyval'
import { isEmpty } from 'lodash'
import { getHashDataId, submitRemixMap } from 'map-creator'
import { useFilterResults, useHasFilters } from 'map-filter-view'
import { useAuthWorkspaces } from 'user-auth'
import { createLocalMap, getMapLink, getNanoId, withPostfix } from 'utils'

import { useMap } from '~map-preview/states/map'

import { CreateMapInput } from '~map-preview/types/__generated/gql/graphql'
import { Values } from './types'

interface FormProps {
  children?: React.ReactNode
  onSubmit?: () => void
  onSubmitComplete?: (event: { redirect: string }) => void
  onClose?: () => void
}

function Form(props: FormProps) {
  const { onClose, onSubmit, onSubmitComplete } = props

  const workspaces = useAuthWorkspaces()
  const hasFilters = useHasFilters()

  const parentMap = useMap()
  const filterResults = useFilterResults()

  if (!workspaces) {
    return null
  }

  return (
    <Formik<Values>
      initialValues={{
        filters: hasFilters,
        workspace: workspaces[0]!,
        includeProperties: []
      }}
      onSubmit={async (values) => {
        const { filters, workspace, includeProperties } = values
        const applyFilters = filters && filterResults.length > 0
        const geojson = await get(parentMap.dataId)
        geojson.features = applyFilters ? filterResults : geojson.features
        const inputGeojson = filters
          ? getFilterGeojson(geojson, includeProperties)
          : geojson
        const propertyNames = toPropertyNames(parentMap.properties)
        const dataId = getHashDataId(inputGeojson, propertyNames)!
        const bbox = getBbox(inputGeojson)
        const {
          geometry: { coordinates: centroid }
        } = getCentroid(inputGeojson)

        const childMap: CreateMapInput = {
          id: getNanoId(),
          dataId,
          workspaceId: workspace.id,
          name: withPostfix(parentMap.name, ' (copy)'),
          properties: getInputProperties(parentMap.properties),
          type: parentMap.type,
          bbox,
          centroid,
          views: 1,
          likes: 0,
          remixes: 0
        }

        onSubmit?.()
        onClose?.()

        await createLocalMap(inputGeojson, childMap)
        await submitRemixMap(inputGeojson, parentMap, childMap)

        onSubmitComplete?.({
          redirect: getMapLink({ mapId: childMap.id, mapName: childMap.name })
        })
      }}
    >
      {(formik) => {
        return <form onSubmit={formik.handleSubmit}>{props?.children}</form>
      }}
    </Formik>
  )
}

const getInputProperties = (properties) => {
  return properties.map((property) => {
    const { options, classBreaks, ...rest } = property
    if (!isEmpty(options)) rest.options = options
    if (!isEmpty(classBreaks)) rest.classBreaks = classBreaks
    return rest
  })
}

const getFilterGeojson = (geojson, properties) => {
  for (const feature of geojson.features) {
    for (const property of properties) {
      delete feature.properties[property.name]
    }
  }
  return geojson
}

const toPropertyNames = (properties) => {
  return Object.fromEntries(
    properties.map((property) => [property.name, property.name])
  )
}

export { Form }
